import { LanCode } from "@/store/reducers/setting";
import { SeoData } from "@/types/seo";

/**
 * 제품 PDP 의 **비영어** title · description (#6104) — 제품 slug 별.
 *
 * 영어는 여기 두지 않는다 — `generateProductSeo` 가 종전대로 어드민 `shortDesc` 를 쓴다. 어드민의 언어별 배너 필드
 * (`bannerTitle` · `bannerDescription`)는 비어 있는 제품이 많고 제품명을 음차한 값도 있어 메타로 쓰지 않는다
 * (2026-10-29 prod 실측: Pro 제목 · Air 설명 · TactVisor 둘 다 빈 값, Tactosy for Feet 「발용 택토시」).
 *
 * 문구는 사이트에 이미 있는 한 줄 소개를 따른다 — Pro/Air 는 GNB 「Premium haptic suit for PC and VR gaming」 ·
 * 「Lightweight haptic suit optimized for PC gaming」, Camo 는 「Gear Up for Battle. TactSuit Air Camo Edition.」의
 * 각 언어 번역값. 수치는 어드민 `shortDesc` 와 같다(Pro 32 · Air 16 · Sleeve 팔당 3 · DK3 손당 8 · Visor 4 · Feet 발당 3).
 * 제품명은 영문 공식 표기 그대로 둔다.
 */
export const productSeoData: Record<
  string,
  Partial<Record<LanCode, SeoData>>
> = {
  "tactsuit-pro": {
    [LanCode.Korean]: {
      title: "TactSuit Pro — 프리미엄 햅틱 수트 | 비햅틱스",
      description:
        "PC/VR 게임 몰입을 완성하는 프리미엄 햅틱 수트. 32개 진동 모터로 게임 속 사운드와 액션을 온몸으로 느껴보세요.",
    },
    [LanCode.Japanese]: {
      title: "TactSuit Pro — プレミアムハプティクススーツ | bHaptics",
      description:
        "PC・VRゲーム向けのプレミアムハプティクススーツ。32個の振動モーターで、ゲームのサウンドとアクションを全身で体感できます。",
    },
    [LanCode.Chinese]: {
      title: "TactSuit Pro — 高端触感套装 | bHaptics",
      description:
        "面向 PC 与 VR 游戏的高端触感套装。32 个振动马达，让你全身感受游戏中的声音与动作。",
    },
    [LanCode.German]: {
      title: "TactSuit Pro — Premium-Haptik-Anzug | bHaptics",
      description:
        "Premium-Haptik-Anzug für PC- und VR-Gaming. Mit 32 Vibrationsmotoren spürst du Sound und Action deiner Spiele am ganzen Körper.",
    },
    [LanCode.French]: {
      title: "TactSuit Pro — Gilet haptique premium | bHaptics",
      description:
        "Gilet haptique premium pour le jeu PC et VR. Ses 32 moteurs de vibration vous font ressentir le son et l'action de vos jeux dans tout le corps.",
    },
    [LanCode.Spanish]: {
      title: "TactSuit Pro — Chaleco háptico premium | bHaptics",
      description:
        "Chaleco háptico premium para juegos de PC y VR. Sus 32 motores de vibración te hacen sentir el sonido y la acción de tus juegos con todo el cuerpo.",
    },
  },
  "tactsuit-air": {
    [LanCode.Korean]: {
      title: "TactSuit Air — 가볍게 시작하는 햅틱 수트 | 비햅틱스",
      description:
        "가볍게 시작하는 PC 게임 최적 햅틱 수트. 16개 진동 모터로 게임 속 사운드와 액션을 온몸으로 느껴보세요.",
    },
    [LanCode.Japanese]: {
      title: "TactSuit Air — 軽量ハプティクススーツ | bHaptics",
      description:
        "PCゲームに最適化された軽量ハプティクススーツ。16個の振動モーターで、ゲームのサウンドとアクションを全身で体感できます。",
    },
    [LanCode.Chinese]: {
      title: "TactSuit Air — 轻量触感套装 | bHaptics",
      description:
        "专为 PC 游戏优化的轻量触感套装。16 个振动马达，让你全身感受游戏中的声音与动作。",
    },
    [LanCode.German]: {
      title: "TactSuit Air — Leichter Haptik-Anzug | bHaptics",
      description:
        "Leichter Haptik-Anzug, optimiert für PC-Gaming. Mit 16 Vibrationsmotoren spürst du Sound und Action deiner Spiele am ganzen Körper.",
    },
    [LanCode.French]: {
      title: "TactSuit Air — Gilet haptique léger | bHaptics",
      description:
        "Gilet haptique léger optimisé pour le jeu PC. Ses 16 moteurs de vibration vous font ressentir le son et l'action de vos jeux dans tout le corps.",
    },
    [LanCode.Spanish]: {
      title: "TactSuit Air — Chaleco háptico ligero | bHaptics",
      description:
        "Chaleco háptico ligero optimizado para juegos de PC. Sus 16 motores de vibración te hacen sentir el sonido y la acción de tus juegos con todo el cuerpo.",
    },
  },
  "tactsuit-air-camo": {
    [LanCode.Korean]: {
      title: "TactSuit Air: Camo — 카모 에디션 햅틱 수트 | 비햅틱스",
      description:
        "전투를 위한 준비. TactSuit Air Camo Edition. 16개 진동 모터로 게임 속 사운드와 액션을 온몸으로 느껴보세요.",
    },
    [LanCode.Japanese]: {
      title: "TactSuit Air: Camo — カモエディション | bHaptics",
      description:
        "戦いの準備を整えよう。TactSuit Air Camo Edition。16個の振動モーターで、ゲームを全身で体感できます。",
    },
    [LanCode.Chinese]: {
      title: "TactSuit Air: Camo — 迷彩版 | bHaptics",
      description:
        "装备好准备战斗。TactSuit Air Camo Edition。16 个振动马达，让你全身感受游戏。",
    },
    [LanCode.German]: {
      title: "TactSuit Air: Camo — Camo Edition | bHaptics",
      description:
        "Rüste dich für die Schlacht aus. TactSuit Air Camo Edition. Mit 16 Vibrationsmotoren spürst du deine Spiele am ganzen Körper.",
    },
    [LanCode.French]: {
      title: "TactSuit Air: Camo — Édition Camo | bHaptics",
      description:
        "Préparez-vous pour la bataille. TactSuit Air Camo Edition. Ses 16 moteurs de vibration vous font ressentir vos jeux dans tout le corps.",
    },
    [LanCode.Spanish]: {
      title: "TactSuit Air: Camo — Edición Camo | bHaptics",
      description:
        "Prepárate para la batalla. TactSuit Air Camo Edition. Sus 16 motores de vibración te hacen sentir tus juegos con todo el cuerpo.",
    },
  },
  tactsleeve: {
    [LanCode.Korean]: {
      title: "TactSleeve — 팔 햅틱 슬리브 | 비햅틱스",
      description:
        "팔마다 3개 진동 모터로 정밀한 햅틱을 더하는 TactSleeve. TactSuit와 함께 팔까지 몰입을 이어가세요.",
    },
    [LanCode.Japanese]: {
      title: "TactSleeve — 腕用ハプティクススリーブ | bHaptics",
      description:
        "片腕3個の振動モーターで精密なハプティクスを加えるTactSleeve。TactSuitと組み合わせて、腕まで没入感を広げましょう。",
    },
    [LanCode.Chinese]: {
      title: "TactSleeve — 手臂触感套袖 | bHaptics",
      description:
        "每只手臂 3 个振动马达，带来精准触感的 TactSleeve。与 TactSuit 搭配，把沉浸感延伸到手臂。",
    },
    [LanCode.German]: {
      title: "TactSleeve — Haptische Armmanschette | bHaptics",
      description:
        "TactSleeve bringt mit 3 Vibrationsmotoren pro Arm präzise Haptik. Zusammen mit der TactSuit reicht die Immersion bis in die Arme.",
    },
    [LanCode.French]: {
      title: "TactSleeve — Manchon haptique pour les bras | bHaptics",
      description:
        "TactSleeve ajoute une haptique précise avec 3 moteurs de vibration par bras. Associé au TactSuit, l'immersion s'étend jusqu'aux bras.",
    },
    [LanCode.Spanish]: {
      title: "TactSleeve — Manga háptica para brazos | bHaptics",
      description:
        "TactSleeve añade háptica precisa con 3 motores de vibración por brazo. Junto con TactSuit, la inmersión llega hasta los brazos.",
    },
  },
  "tactglove-dk3": {
    [LanCode.Korean]: {
      title: "TactGlove DK3 — 햅틱 장갑 | 비햅틱스",
      description:
        "손끝으로 전해지는 몰입감. 손마다 8개 햅틱 포인트를 갖춘 TactGlove DK3로 XR 훈련, 시뮬레이션, 연구를 위한 정밀한 햅틱을 경험하세요.",
    },
    [LanCode.Japanese]: {
      title: "TactGlove DK3 — ハプティクスグローブ | bHaptics",
      description:
        "指先に伝わる没入感。片手8つのハプティックポイントを備えたTactGlove DK3で、XRトレーニングやシミュレーション、研究向けの精密なハプティクスを体験できます。",
    },
    [LanCode.Chinese]: {
      title: "TactGlove DK3 — 触感手套 | bHaptics",
      description:
        "指尖传来的沉浸感。每只手 8 个触感点的 TactGlove DK3，为 XR 训练、模拟和研究带来精准触感。",
    },
    [LanCode.German]: {
      title: "TactGlove DK3 — Haptischer Handschuh | bHaptics",
      description:
        "Immersion bis in die Fingerspitzen. TactGlove DK3 mit 8 haptischen Punkten pro Hand liefert präzise Haptik für XR-Training, Simulation und Forschung.",
    },
    [LanCode.French]: {
      title: "TactGlove DK3 — Gant haptique | bHaptics",
      description:
        "L'immersion au bout des doigts. Avec 8 points haptiques par main, TactGlove DK3 offre une haptique précise pour la formation XR, la simulation et la recherche.",
    },
    [LanCode.Spanish]: {
      title: "TactGlove DK3 — Guante háptico | bHaptics",
      description:
        "Inmersión en la punta de los dedos. Con 8 puntos hápticos por mano, TactGlove DK3 ofrece háptica precisa para formación XR, simulación e investigación.",
    },
  },
  tactvisor: {
    [LanCode.Korean]: {
      title: "TactVisor — VR HMD용 페이스 햅틱 커버 | 비햅틱스",
      description:
        "VR HMD에 부착하는 페이스 햅틱 커버 TactVisor. 4개 진동 모터로 가상 세계를 직접 느끼며 새로운 몰입을 경험하세요.",
    },
    [LanCode.Japanese]: {
      title: "TactVisor — VR HMD用フェイスハプティクスカバー | bHaptics",
      description:
        "VR HMDに取り付けるフェイスハプティクスカバーTactVisor。4個の振動モーターで仮想世界を直接感じる、新しい没入感を体験できます。",
    },
    [LanCode.Chinese]: {
      title: "TactVisor — VR 头显面部触感罩 | bHaptics",
      description:
        "安装在 VR 头显上的面部触感罩 TactVisor。4 个振动马达，让你亲身感受虚拟世界，体验全新的沉浸感。",
    },
    [LanCode.German]: {
      title:
        "TactVisor — Haptische Gesichtsabdeckung für VR-Headsets | bHaptics",
      description:
        "TactVisor ist eine haptische Gesichtsabdeckung für dein VR-Headset. Mit 4 Vibrationsmotoren spürst du die virtuelle Welt direkt.",
    },
    [LanCode.French]: {
      title: "TactVisor — Couvre-visage haptique pour casque VR | bHaptics",
      description:
        "TactVisor est un couvre-visage haptique qui se fixe sur votre casque VR. Ses 4 moteurs de vibration vous font ressentir directement le monde virtuel.",
    },
    [LanCode.Spanish]: {
      title: "TactVisor — Cubierta facial háptica para visores VR | bHaptics",
      description:
        "TactVisor es una cubierta facial háptica que se coloca en tu visor VR. Sus 4 motores de vibración te permiten sentir el mundo virtual directamente.",
    },
  },
  // 판매 종료지만 사이트맵에 있어 색인된다(2026-10-29 prod sitemap 실측) — 설명은 어드민 `shortDesc` 의 기존 번역값.
  "tactosy-for-feet": {
    [LanCode.Korean]: {
      title: "Tactosy for Feet — 발 햅틱 디바이스 | 비햅틱스",
      description: "각 발에 3개의 피드백 포인트가 있는 발 전용 햅틱 장치.",
    },
    [LanCode.Japanese]: {
      title: "Tactosy for Feet — 足用ハプティックデバイス | bHaptics",
      description:
        "各足に3つのフィードバックポイントを備えた足用ハプティックデバイス。",
    },
    [LanCode.Chinese]: {
      title: "Tactosy for Feet — 足部触觉设备 | bHaptics",
      description: "足部触觉设备，每只脚有3个反馈点。",
    },
    [LanCode.German]: {
      title: "Tactosy for Feet — Haptisches Gerät für Füße | bHaptics",
      description:
        "Haptische Geräte für Füße mit 3 Rückkopplungspunkten pro Fuß.",
    },
    [LanCode.French]: {
      title: "Tactosy for Feet — Dispositif haptique pour les pieds | bHaptics",
      description:
        "Dispositifs haptiques pour les pieds avec 3 points de retour d'information pour chaque pied.",
    },
    [LanCode.Spanish]: {
      title: "Tactosy for Feet — Dispositivo háptico para pies | bHaptics",
      description:
        "Dispositivos hápticos para pies con 3 puntos de retroalimentación para cada pie.",
    },
  },
};
